/**
 * Custom Main Layout Component
 *
 * Implements the admin panel layout structure with:
 * - Fixed header with logo, global search, and user menu
 * - Collapsible sidebar with navigation menu
 * - Main content area with breadcrumbs
 * - Responsive behavior for mobile/tablet screens
 *
 * Requirements:
 * - Task 35: Main layout with header and sidebar
 * - Uses Ant Design Layout components
 * - Integrates Refine useMenu hook for navigation
 */

import React, { useState } from "react";
import { useMenu, useGetIdentity, useLogout, useNavigation } from "@refinedev/core";
import { Layout as AntdLayout, Menu, Avatar, Dropdown, Button, Space, theme } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { GlobalSearch } from "./GlobalSearch";

const { Header, Sider, Content } = AntdLayout;

interface MainLayoutProps {
  children: React.ReactNode;
}

/**
 * Main layout component that wraps all authenticated pages.
 *
 * Features:
 * - Collapsible sidebar with menu items from Refine resources
 * - Header with logo, search placeholder, and user menu
 * - Responsive design with mobile breakpoint
 * - Ant Design token-based theming
 */
export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { menuItems, selectedKey } = useMenu();
  const { data: identity } = useGetIdentity<{ name: string; avatar?: string }>();
  const { mutate: logout } = useLogout();
  const { show } = useNavigation();
  const { token } = theme.useToken();

  /**
   * Handle user selection from global search
   * Navigates to user detail page
   */
  const handleUserSelect = (userId: number) => {
    show("users", userId);
  };

  // User menu items for dropdown
  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Профиль",
      disabled: true, // Future enhancement
    },
    {
      type: "divider" as const,
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Выйти",
      onClick: () => logout(),
    },
  ];

  return (
    <AntdLayout style={{ minHeight: "100vh" }}>
      {/* Sidebar with navigation menu */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        width={240}
        style={{
          overflow: "auto",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        {/* Logo section */}
        <div
          style={{
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 16px",
            borderBottom: `1px solid ${token.colorBorder}`,
          }}
        >
          {!collapsed ? (
            <h1
              style={{
                margin: 0,
                color: token.colorPrimary,
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              GeekTime CRM
            </h1>
          ) : (
            <h1
              style={{
                margin: 0,
                color: token.colorPrimary,
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              GT
            </h1>
          )}
        </div>

        {/* Navigation menu from Refine resources */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          items={menuItems.map((item) => ({
            key: item.key,
            icon: item.icon,
            label: <Link to={item.route || "/"}>{item.label}</Link>,
          }))}
          style={{ marginTop: "16px" }}
        />
      </Sider>

      {/* Main layout with header and content */}
      <AntdLayout style={{ marginLeft: collapsed ? 80 : 240, transition: "margin 0.2s" }}>
        {/* Header */}
        <Header
          style={{
            padding: "0 24px",
            background: token.colorBgContainer,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 1px 4px rgba(0,21,41,.08)",
            position: "sticky",
            top: 0,
            zIndex: 999,
          }}
        >
          <Space size="large">
            {/* Collapse toggle button */}
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: "16px",
                width: 40,
                height: 40,
              }}
            />

            {/* Global Search Component - Task 40 */}
            <GlobalSearch onSelect={handleUserSelect} />
          </Space>

          {/* User menu */}
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
            <Space style={{ cursor: "pointer" }}>
              <Avatar icon={<UserOutlined />} src={identity?.avatar} />
              {identity?.name && (
                <span style={{ fontWeight: 500 }}>{identity.name}</span>
              )}
            </Space>
          </Dropdown>
        </Header>

        {/* Main content area */}
        <Content
          style={{
            margin: "24px 16px",
            padding: 24,
            minHeight: 280,
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
          }}
        >
          {children}
        </Content>
      </AntdLayout>
    </AntdLayout>
  );
};
